import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { API_BASE } from "@/lib/api";
import { devLog } from "@/lib/log";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Loader2, Share2, X } from "lucide-react";
import * as pdfjs from "pdfjs-dist/legacy/build/pdf";

// Worker is served from public/pdfjs/ so it works offline (SW cached)
// and is version-locked to the installed pdfjs-dist package. No CDN
// round-trip on first preview, and no fake-worker main-thread stall.
pdfjs.GlobalWorkerOptions.workerSrc = "/pdfjs/pdf.worker.min.js";

/**
 * PdfPreviewSheet — full-screen bottom sheet that fetches a PDF blob,
 * renders one page at a time to a `<canvas>` scaled to container width
 * (no horizontal pan at default zoom), and surfaces:
 *
 *   • header:  Title + "Page X of Y"
 *   • body:    single page rendered via pdfjs at container width
 *   • nav:     Prev / Next buttons (only when totalPages > 1) + swipe
 *   • actions: Close + Share (Share only fires from HERE — never on
 *              the first Download tap that mounted this sheet)
 *
 * If pdfjs fails to open the file (very rare — usually a corrupt PDF),
 * we fall back to a plain `<iframe src={blobUrl}>` so the tradie still
 * has SOMETHING to look at before tapping Share.
 */
export default function PdfPreviewSheet({
  open,
  onOpenChange,
  pdfUrl,
  filename = "buildintel.pdf",
  title = "Preview PDF",
  description,
  shareText = "",
  shareSubject = "",
  testid = "pdf-preview-sheet",
}) {
  const [blobUrl, setBlobUrl] = useState(null);
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sharing, setSharing] = useState(false);

  // pdfjs state
  const [pdfDoc, setPdfDoc] = useState(null);
  const [numPages, setNumPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pdfError, setPdfError] = useState(false);
  const [rendering, setRendering] = useState(false);

  const objectUrlRef = useRef(null);
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  // Guards against overlapping renderPage calls (page-flip while a
  // previous render is mid-flight leaves pdfjs in a bad state).
  const renderTaskRef = useRef(null);

  /* --------------------------- Fetch + parse --------------------------- */

  useEffect(() => {
    if (!open || !pdfUrl) return;
    let cancelled = false;

    setLoading(true);
    setBlobUrl(null);
    setFile(null);
    setPdfDoc(null);
    setNumPages(0);
    setCurrentPage(1);
    setPdfError(false);

    const absolute = pdfUrl.startsWith("http")
      ? pdfUrl
      : `${API_BASE}${pdfUrl.startsWith("/api") ? pdfUrl : `/api${pdfUrl}`}`;

    (async () => {
      try {
        const res = await fetch(absolute, { credentials: "include" });
        if (!res.ok) throw new Error(`Download failed (${res.status})`);
        const blob = await res.blob();
        if (cancelled) return;
        const f = new File([blob], filename, { type: "application/pdf" });
        const url = URL.createObjectURL(blob);
        objectUrlRef.current = url;
        setBlobUrl(url);
        setFile(f);

        // Kick off pdfjs parse — this yields page count so we can render
        // the header + Prev/Next buttons before drawing anything.
        try {
          const bytes = await blob.arrayBuffer();
          if (cancelled) return;
          const loadingTask = pdfjs.getDocument({ data: bytes });
          const doc = await loadingTask.promise;
          if (cancelled) { try { doc.destroy(); } catch { /* noop */ } return; }
          setPdfDoc(doc);
          setNumPages(doc.numPages || 0);
        } catch (err) {
          devLog.warn("PdfPreviewSheet pdfjs parse failed → iframe fallback", err);
          if (!cancelled) setPdfError(true);
        }
      } catch (err) {
        devLog.warn("PdfPreviewSheet fetch failed", err);
        if (!cancelled) {
          toast.error("Couldn't build the PDF — try again");
          onOpenChange(false);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      if (renderTaskRef.current) {
        try { renderTaskRef.current.cancel(); } catch { /* noop */ }
        renderTaskRef.current = null;
      }
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
        objectUrlRef.current = null;
      }
    };
  }, [open, pdfUrl, filename, onOpenChange]);

  /* --------------------------- Render page --------------------------- */

  const renderPage = useCallback(async (pageNum) => {
    if (!pdfDoc || !canvasRef.current || !containerRef.current) return;
    setRendering(true);
    try {
      // Cancel any in-flight render before starting a new one.
      if (renderTaskRef.current) {
        try { renderTaskRef.current.cancel(); } catch { /* noop */ }
        renderTaskRef.current = null;
      }
      const page = await pdfDoc.getPage(pageNum);
      const viewport1 = page.getViewport({ scale: 1 });
      // Fit page WIDTH to container. `clientWidth` already excludes the
      // scrollbar so one page renders edge-to-edge without horizontal pan.
      const containerWidth = containerRef.current.clientWidth || 320;
      const scale = containerWidth / viewport1.width;
      const viewport = page.getViewport({ scale });
      // Retina crispness — draw at devicePixelRatio, then let CSS scale
      // the canvas back down. Cap at 2.5x so we don't nuke iOS memory
      // on a 3x device rendering a huge multi-page PDF.
      const dpr = Math.min(window.devicePixelRatio || 1, 2.5);
      const canvas = canvasRef.current;
      const ctx = canvas.getContext("2d");
      canvas.width = Math.floor(viewport.width * dpr);
      canvas.height = Math.floor(viewport.height * dpr);
      canvas.style.width = `${Math.floor(viewport.width)}px`;
      canvas.style.height = `${Math.floor(viewport.height)}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      const task = page.render({ canvasContext: ctx, viewport });
      renderTaskRef.current = task;
      await task.promise;
      renderTaskRef.current = null;
    } catch (err) {
      // "RenderingCancelledException" is expected on rapid page flips.
      if (err && err.name !== "RenderingCancelledException") {
        devLog.warn("PdfPreviewSheet render failed", err);
      }
    } finally {
      setRendering(false);
    }
  }, [pdfDoc]);

  useEffect(() => {
    if (pdfDoc && !pdfError) renderPage(currentPage);
  }, [pdfDoc, currentPage, pdfError, renderPage]);

  // Re-render on window resize so a rotated phone doesn't leave a
  // stale-width page. Debounced with rAF.
  useEffect(() => {
    if (!pdfDoc || pdfError) return;
    let raf;
    const onResize = () => {
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => renderPage(currentPage));
    };
    window.addEventListener("resize", onResize);
    window.addEventListener("orientationchange", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("orientationchange", onResize);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [pdfDoc, pdfError, currentPage, renderPage]);

  /* ----------------------------- Swipe nav ----------------------------- */

  const touchStartX = useRef(null);
  const touchStartY = useRef(null);
  const onTouchStart = (e) => {
    const t = e.touches?.[0];
    if (!t) return;
    touchStartX.current = t.clientX;
    touchStartY.current = t.clientY;
  };
  const onTouchEnd = (e) => {
    const startX = touchStartX.current;
    const startY = touchStartY.current;
    touchStartX.current = null;
    touchStartY.current = null;
    if (startX == null) return;
    const t = e.changedTouches?.[0];
    if (!t) return;
    const dx = t.clientX - startX;
    const dy = t.clientY - startY;
    // Only treat as a page-flip swipe when the horizontal motion dominates
    // and clears a threshold — otherwise it's a vertical scroll on the
    // page, which is allowed to bubble to the container.
    if (Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy) * 1.5) {
      if (dx < 0) goNext();
      else goPrev();
    }
  };

  const goPrev = useCallback(() => {
    setCurrentPage((p) => Math.max(1, p - 1));
  }, []);
  const goNext = useCallback(() => {
    setCurrentPage((p) => Math.min(numPages || 1, p + 1));
  }, [numPages]);

  /* ------------------------------- Share ------------------------------- */

  const canShareFile = useMemo(() => (
    typeof navigator !== "undefined"
    && typeof navigator.canShare === "function"
    && typeof navigator.share === "function"
    && !!file
    && navigator.canShare({ files: [file] })
  ), [file]);

  const handleShare = async () => {
    if (sharing) return;
    setSharing(true);
    try {
      if (canShareFile) {
        try {
          await navigator.share({
            files: [file],
            title: shareSubject || filename,
            text: shareText || "",
          });
          return;
        } catch (err) {
          if (err && err.name === "AbortError") return;
          devLog.warn("PdfPreviewSheet share failed → fallback download", err);
        }
      }
      if (blobUrl) {
        const a = document.createElement("a");
        a.href = blobUrl;
        a.download = filename;
        a.rel = "noopener";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        toast.success("PDF saved — attach it from Files");
      }
    } finally {
      setSharing(false);
    }
  };

  const pageLabel = numPages > 0 ? `Page ${currentPage} of ${numPages}` : null;
  const showPageNav = !pdfError && numPages > 1;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="bottom"
        data-testid={testid}
        className="bg-[#111214] border-t border-white/10 text-white p-0 h-[100dvh] max-h-[100dvh] rounded-t-2xl flex flex-col"
      >
        <SheetHeader className="px-4 pt-3 pb-3 border-b border-white/10">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <SheetTitle
                data-testid={`${testid}-title`}
                className="font-heading font-extrabold text-base sm:text-lg text-white leading-tight truncate"
              >
                {title}
              </SheetTitle>
              {description && (
                <SheetDescription className="text-white/50 text-xs">
                  {description}
                </SheetDescription>
              )}
            </div>
            {pageLabel && (
              <div
                data-testid={`${testid}-page-label`}
                className="shrink-0 text-[11px] font-mono uppercase tracking-wider text-white/70 bg-white/5 border border-white/10 rounded-full px-2.5 py-1"
              >
                {pageLabel}
              </div>
            )}
          </div>
        </SheetHeader>

        <div
          ref={containerRef}
          className="flex-1 min-h-0 bg-[#0A0A0B] relative overflow-y-auto overflow-x-hidden"
          data-testid={`${testid}-body`}
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
        >
          {loading && (
            <div className="absolute inset-0 flex flex-col items-center justify-center text-white/60 gap-2 z-10">
              <Loader2 className="w-6 h-6 animate-spin text-[#FF9500]" />
              <div className="text-[12px]">Building your PDF…</div>
            </div>
          )}

          {/* Primary: pdfjs canvas — one page at a time, fitted to container width */}
          {!pdfError && !loading && (
            <div className="flex items-start justify-center p-2 relative">
              <canvas
                ref={canvasRef}
                data-testid={`${testid}-canvas`}
                className="block max-w-full h-auto shadow-[0_10px_30px_-10px_rgba(0,0,0,0.6)]"
              />
              {rendering && (
                <div className="absolute top-2 right-2 rounded-full bg-black/60 backdrop-blur px-2 py-1 text-[10px] uppercase tracking-wider text-white/70 flex items-center gap-1">
                  <Loader2 className="w-3 h-3 animate-spin" /> Rendering
                </div>
              )}
            </div>
          )}

          {/* Fallback: raw iframe if pdfjs couldn't open the file */}
          {pdfError && blobUrl && !loading && (
            <iframe
              key={blobUrl}
              src={blobUrl}
              title={title}
              data-testid={`${testid}-iframe`}
              className="w-full h-full border-0"
            />
          )}
        </div>

        {showPageNav && (
          <div
            className="grid grid-cols-2 gap-2 px-3 py-2 border-t border-white/10 bg-[#0F0F10]"
            data-testid={`${testid}-page-nav`}
          >
            <button
              type="button"
              data-testid={`${testid}-prev`}
              onClick={goPrev}
              disabled={currentPage <= 1 || rendering}
              className="h-10 rounded-xl bi-ghost-btn text-xs font-heading font-bold uppercase tracking-wider inline-flex items-center justify-center gap-2 disabled:opacity-40"
            >
              <ChevronLeft className="w-4 h-4" /> Prev
            </button>
            <button
              type="button"
              data-testid={`${testid}-next`}
              onClick={goNext}
              disabled={currentPage >= numPages || rendering}
              className="h-10 rounded-xl bi-ghost-btn text-xs font-heading font-bold uppercase tracking-wider inline-flex items-center justify-center gap-2 disabled:opacity-40"
            >
              Next <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}

        <div
          className="grid grid-cols-2 gap-2 p-3 border-t border-white/10 bg-[#1F1F1F]"
          style={{ paddingBottom: "calc(0.75rem + env(safe-area-inset-bottom))" }}
          data-testid={`${testid}-actions`}
        >
          <button
            type="button"
            data-testid={`${testid}-close`}
            onClick={() => onOpenChange(false)}
            className="h-12 rounded-xl bi-ghost-btn text-sm font-heading font-bold uppercase tracking-wider inline-flex items-center justify-center gap-2"
          >
            <X className="w-4 h-4" /> Close
          </button>
          <button
            type="button"
            data-testid={`${testid}-share`}
            onClick={handleShare}
            disabled={!file || sharing}
            className="h-12 rounded-xl bi-accent-btn text-sm font-heading font-bold uppercase tracking-wider inline-flex items-center justify-center gap-2 disabled:opacity-60 shadow-[0_10px_30px_-15px_rgba(255,149,0,0.6)]"
          >
            {sharing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Share2 className="w-4 h-4" />}
            Share
          </button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
